/**
 * /music-stand — the stand itself: what is on the screen, how pages turn,
 * and every way to turn them (taps, swipes, keys and pedals, head gestures
 * from gestures.js). The decisions live in core.js; this file draws them.
 *
 * Instant turns are the whole point on stage, so pages are rendered AHEAD:
 * after every turn the next two pages (and the first page of the next piece
 * in a setlist) are rendered at the current size in the background. A page
 * that is not ready yet is shown from its 640-px analysis copy and replaced
 * the moment the sharp one arrives — never a blank screen.
 */
import {
    layoutView, pageToScreen, screenToPage, Navigator, uniformBoxes, findSplit, segmentBreaks,
    actionForKey, keyId, createDebouncer, DEFAULT_KEYS,
} from './core.js';
import { openSource, analyzePage } from './source.js';
import { createInk } from './ink.js';

const FULL = { x: 0, y: 0, w: 1, h: 1 };

export function createReader(S) {
    const els = S.els;
    const R = {
        els, pieces: [], seq: [], starts: [], nav: null, open: false,
        mode: 'read', locked: false, perf: false, pseudo: false,
        cache: new Map(), pending: new Map(), queue: [], busy: false,
        tiles: [], divider: null, W: 0, H: 0, dpr: 1,
        turns: 0, via: {}, completed: false, segCache: new Map(),
        visible: true, setlist: null,
    };

    /* ── per-page facts ──────────────────────────────────────────────── */

    function entryOf(i) { return R.seq[i]; }
    function sizeOf(k, p) {
        const P = R.pieces[k];
        const an = P.an[p];
        if (an && an.pw) return { pw: an.pw, ph: an.ph };
        const ks = P.src && P.src.knownSize(p);
        if (ks) return ks;
        const first = P.an.find((a) => a && a.pw);
        return first ? { pw: first.pw, ph: first.ph } : { pw: 595, ph: 842 };
    }
    R.metaOf = function metaOf(i) {
        const e = entryOf(i);
        if (!e) return { pw: 595, ph: 842, box: FULL, split: 0.5 };
        const P = R.pieces[e.k];
        const an = P.an[e.p];
        const s = sizeOf(e.k, e.p);
        const box = S.settings.crop && P.boxes && P.boxes[e.p] ? P.boxes[e.p] : FULL;
        const split = an ? (an.splitUser ?? an.split ?? null) : null;
        return { pw: s.pw, ph: s.ph, box, split };
    };

    function effMode() {
        const m = S.settings.mode;
        if (m === 'auto') return (R.W / Math.max(1, R.H) > 1.2 && R.W >= 700) ? 'spread' : 'page';
        return m;
    }

    function segsOf(i) {
        const e = entryOf(i);
        if (!e) return [[0, 1]];
        const m = R.metaOf(i);
        const key = `${i}|${R.W}|${R.H}|${S.settings.crop ? 1 : 0}|${m.box.w}|${m.box.h}`;
        if (R.segCache.has(key)) return R.segCache.get(key);
        const b = m.box;
        const s = R.W / (b.w * m.pw);
        const maxSpan = R.H / (s * m.ph);
        const an = R.pieces[e.k].an[e.p];
        let segs;
        if (an && an.prof) segs = segmentBreaks(an.prof, b, maxSpan);
        else {
            const n = Math.max(1, Math.ceil(b.h / maxSpan - 0.02));
            segs = [];
            for (let k = 0; k < n; k++) segs.push([b.y + b.h * k / n, b.y + b.h * (k + 1) / n]);
        }
        if (R.segCache.size > 400) R.segCache.clear();
        R.segCache.set(key, segs);
        return segs;
    }

    function linksOf(i) {
        const e = entryOf(i);
        if (!e) return [];
        const P = R.pieces[e.k];
        const base = R.starts[e.k];
        return (P.rec.links || []).filter((l) => l.from === e.p).map((l) => ({ id: l.id, to: base + l.to, pass: l.pass | 0 }));
    }

    const ctx = {
        get count() { return R.seq.length; },
        pieceStart: (i) => R.starts[R.seq[i].k],
        pieceEnd: (i) => R.starts[R.seq[i].k] + R.pieces[R.seq[i].k].rec.pages,
        get mode() { return effMode(); },
        get half() { return !!S.settings.half; },
        get spreadStep() { return S.settings.spreadStep === 1 ? 1 : 2; },
        segs: segsOf,
        links: linksOf,
    };

    /* ── loading ─────────────────────────────────────────────────────── */

    function ensureSource(k) {
        const P = R.pieces[k];
        if (!P) return Promise.reject(new Error('no piece'));
        if (!P.srcP) {
            P.srcP = S.store.byPiece('files', P.rec.id).then((files) => {
                if (!files || !files.length) throw Object.assign(new Error('files missing'), { msKind: 'missing' });
                return openSource(P.rec, files, S.cfg);
            }).then((src) => { P.src = src; return src; });
            P.srcP.catch((e) => { P.srcP = null; P.failed = e; showMsg(errText(e)); S.diag('piece_open_failed', e, { kind: P.rec.kind }); });
        }
        return P.srcP;
    }

    function errText(e) {
        const n = e && (e.name || '');
        if (/Password/i.test(n)) return S.t('err_pdf_password');
        if (e && e.msKind === 'missing') return S.t('err_missing');
        if (/pdfjs_load/i.test(String(e && e.message))) return S.t('err_pdf_engine');
        return S.t('err_open');
    }

    function recomputeBoxes(k) {
        const P = R.pieces[k];
        P.boxes = uniformBoxes(P.an.map((a) => (a ? a.box : null)));
        R.segCache.clear();
    }

    function ensureAnalysis(k) {
        const P = R.pieces[k];
        if (!P || P.anP) return P && P.anP;
        P.anP = (async () => {
            const saved = await S.store.byPiece('analysis', P.rec.id).catch(() => []);
            for (const a of saved || []) if (a && a.page < P.rec.pages) P.an[a.page] = a;
            recomputeBoxes(k);
            schedule();
            const src = await ensureSource(k);
            const cur = R.nav && entryOf(R.nav.state.i);
            const from = cur && cur.k === k ? cur.p : 0;
            const order = Array.from({ length: P.rec.pages }, (_, p) => p).sort((a, b) => Math.abs(a - from) - Math.abs(b - from));
            let changed = 0;
            for (const p of order) {
                if (!R.open || !R.pieces.includes(P)) return;
                if (P.an[p] && P.an[p].prof) continue;
                try {
                    const a = await analyzePage(src, p);
                    const rec = { piece: P.rec.id, page: p, box: a.box, prof: a.prof, pw: a.pw, ph: a.ph, split: findSplit(a.prof, a.box.full ? null : a.box), splitUser: P.an[p] ? P.an[p].splitUser : undefined };
                    P.an[p] = rec;
                    S.store.put('analysis', rec).catch(() => { /* a cache — recomputed next time */ });
                    const key = k + ':' + p;
                    if (!R.cache.has(key) && near(k, p)) R.cache.set(key, { c: a.lowres, w: a.lowres.width });
                    changed++;
                    if (changed % 3 === 0 || p === from) { recomputeBoxes(k); schedule(); }
                } catch (e) {
                    S.diag('analyze_failed', e);
                }
                await new Promise((r) => setTimeout(r, 0));
            }
            recomputeBoxes(k);
            schedule();
        })();
        P.anP.catch(() => { /* reported by ensureSource */ });
        return P.anP;
    }

    function ensureInk(k) {
        const P = R.pieces[k];
        if (!P || P.inkP) return;
        P.inkP = S.store.byPiece('ink', P.rec.id).then((rows) => {
            // stored marks win over an empty list; a page already drawn on in
            // the meantime keeps what was drawn (it is saved again anyway)
            for (const r of rows || []) { const cur = P.ink.get(r.page); if (!cur || !cur.length) P.ink.set(r.page, r.items || []); }
            schedule();
        }).catch(() => { /* marks just start empty */ });
    }

    function near(k, p) {
        if (!R.nav) return true;
        const cur = entryOf(R.nav.state.i);
        return cur && cur.k === k && Math.abs(cur.p - p) <= 3;
    }

    /* ── rendering pages (ahead of time) ─────────────────────────────── */

    function requestRender(i, need, pri) {
        const e = entryOf(i);
        if (!e) return;
        const key = e.k + ':' + e.p;
        const w = Math.min(4096, Math.max(256, Math.ceil(need / 128) * 128));
        const have = R.cache.get(key);
        if (have && have.w >= need * 0.85) return;
        if ((R.pending.get(key) || 0) >= w * 0.85) return;
        R.pending.set(key, w);
        const job = { key, k: e.k, p: e.p, w };
        if (pri) R.queue.unshift(job); else R.queue.push(job);
        pump();
    }

    async function pump() {
        if (R.busy) return;
        const job = R.queue.shift();
        if (!job) return;
        R.busy = true;
        try {
            const src = await ensureSource(job.k);
            const c = await src.render(job.p, job.w);
            const old = R.cache.get(job.key);
            if (!old || old.w < job.w) {
                R.cache.set(job.key, { c, w: job.w });
                if (old && old.c && old.c !== c && old.c.width) { old.c.width = 0; }
            }
            evict();
            if (R.tiles.some((t) => { const e = entryOf(t.i); return e && e.k + ':' + e.p === job.key; })) schedule();
        } catch (e) {
            if (!(e && e.msKind)) S.diag('render_failed', e);
        } finally {
            R.pending.delete(job.key);
            R.busy = false;
            if (R.queue.length) setTimeout(pump, 0);
        }
    }

    function evict() {
        if (!R.nav) return;
        const cur = R.nav.state.i;
        const keep = new Set();
        // the page before and three ahead: enough for any turn, small enough for an older iPad
        for (let d = -1; d <= 3; d++) { const e = entryOf(cur + d); if (e) keep.add(e.k + ':' + e.p); }
        for (const t of R.tiles) { const e = entryOf(t.i); if (e) keep.add(e.k + ':' + e.p); }
        for (const [key, v] of R.cache) {
            if (keep.has(key)) continue;
            if (v.c && v.c.width) v.c.width = 0;
            R.cache.delete(key);
        }
    }

    function prefetch() {
        if (!R.nav || !R.tiles.length) return;
        const t0 = R.tiles[0];
        const need = t0.dw / t0.sw * R.dpr;
        const cur = R.nav.state.i;
        for (const d of [1, 2, 3, -1]) {
            const i = cur + d;
            if (i >= 0 && i < R.seq.length) {
                requestRender(i, need, false);
                const e = entryOf(i);
                if (e && e.k !== entryOf(cur).k) { ensureAnalysis(e.k); ensureInk(e.k); }
            }
        }
    }

    /* ── drawing ─────────────────────────────────────────────────────── */

    let raf = 0;
    function schedule() {
        if (raf) return;
        raf = requestAnimationFrame(() => { raf = 0; R.render(); });
    }

    function measure() {
        const r = els.stage.getBoundingClientRect();
        const W = Math.max(1, Math.round(r.width)), H = Math.max(1, Math.round(r.height));
        const dpr = Math.min(2.5, window.devicePixelRatio || 1);
        if (W !== R.W || H !== R.H || dpr !== R.dpr) {
            R.W = W; R.H = H; R.dpr = dpr;
            for (const c of [els.page, els.ink, els.fx]) { c.width = Math.round(W * dpr); c.height = Math.round(H * dpr); }
            R.segCache.clear();
            if (R.nav) R.nav.refresh();
        }
    }

    function drawBadge(x2d, n, x, y) {
        const d = R.dpr, r = 13 * d;
        x2d.save();
        x2d.fillStyle = 'rgba(37,99,235,.92)';
        x2d.beginPath(); x2d.arc(x, y, r, 0, Math.PI * 2); x2d.fill();
        x2d.fillStyle = '#fff';
        x2d.font = `700 ${Math.round(13 * d)}px system-ui, -apple-system, sans-serif`;
        x2d.textAlign = 'center'; x2d.textBaseline = 'middle';
        x2d.fillText(String(n), x, y + 0.5 * d);
        x2d.restore();
    }

    R.render = function render() {
        if (!R.open || !R.nav) return;
        measure();
        const view = R.nav.view();
        const lay = layoutView(view, R.metaOf, R.W, R.H, { halfStyle: S.settings.halfStyle });
        R.tiles = lay.tiles; R.divider = lay.divider;
        const d = R.dpr, x = els.page.getContext('2d');
        x.setTransform(1, 0, 0, 1, 0, 0);
        x.clearRect(0, 0, els.page.width, els.page.height);
        let waiting = false;
        for (const t of R.tiles) {
            const e = entryOf(t.i);
            if (!e) continue;
            const key = e.k + ':' + e.p;
            const need = t.dw / t.sw * d;
            const got = R.cache.get(key);
            x.fillStyle = '#fff';
            x.fillRect(t.dx * d, t.dy * d, t.dw * d, t.dh * d);
            if (got && got.c && got.c.width) {
                const cw = got.c.width, ch = got.c.height;
                x.imageSmoothingEnabled = true;
                x.imageSmoothingQuality = 'high';
                x.drawImage(got.c, t.sx * cw, t.sy * ch, t.sw * cw, t.sh * ch, t.dx * d, t.dy * d, t.dw * d, t.dh * d);
            } else waiting = true;
            if (!got || got.w < need * 0.85) requestRender(t.i, need, true);
        }
        // half-page: the divider and page numbers on each half
        if (lay.divider) {
            const dv = lay.divider;
            x.save();
            x.strokeStyle = 'rgba(37,99,235,.9)';
            x.lineWidth = 2 * d;
            x.setLineDash([10 * d, 6 * d]);
            x.beginPath(); x.moveTo(dv.x0 * d, dv.y * d); x.lineTo(dv.x1 * d, dv.y * d); x.stroke();
            x.restore();
            for (const t of R.tiles) {
                const e = entryOf(t.i);
                drawBadge(x, e.p + 1, (t.dx + 16) * d, (t.dy + 16) * d);
            }
        } else if (S.settings.half && effMode() === 'page' && R.tiles.length === 1) {
            // where the next half turn will cut — little marks in the margins
            const t = R.tiles[0], m = R.metaOf(t.i);
            if (m.split != null || R.mode === 'split') {
                const sp = m.split ?? (m.box.y + m.box.h / 2);
                const p = pageToScreen(t, t.sx + 0.0001, sp);
                if (p) {
                    x.save();
                    x.fillStyle = R.mode === 'split' ? 'rgba(37,99,235,1)' : 'rgba(37,99,235,.55)';
                    const yy = p.y * d, s = (R.mode === 'split' ? 11 : 7) * d;
                    x.beginPath(); x.moveTo(t.dx * d, yy - s); x.lineTo(t.dx * d + s * 1.3, yy); x.lineTo(t.dx * d, yy + s); x.fill();
                    const xr = (t.dx + t.dw) * d;
                    x.beginPath(); x.moveTo(xr, yy - s); x.lineTo(xr - s * 1.3, yy); x.lineTo(xr, yy + s); x.fill();
                    if (R.mode === 'split') {
                        x.strokeStyle = 'rgba(37,99,235,.85)'; x.lineWidth = 2 * d; x.setLineDash([8 * d, 6 * d]);
                        x.beginPath(); x.moveTo(t.dx * d, yy); x.lineTo(xr, yy); x.stroke();
                    }
                    x.restore();
                }
            }
        }
        const ix = els.ink.getContext('2d');
        ix.setTransform(1, 0, 0, 1, 0, 0);
        ix.clearRect(0, 0, els.ink.width, els.ink.height);
        R.ink.draw(ix, d);
        placeChips();
        S.onRendered && S.onRendered();
        els.stage.classList.toggle('ms-waiting', waiting);
        prefetch();
    };

    /* ── link chips on the page ──────────────────────────────────────── */

    function placeChips() {
        const box = els.chips;
        box.textContent = '';
        for (const t of R.tiles) {
            const e = entryOf(t.i);
            if (!e) continue;
            const P = R.pieces[e.k];
            for (const l of P.rec.links || []) {
                if (l.from !== e.p) continue;
                const p = pageToScreen(t, l.x ?? (t.sx + t.sw * 0.85), l.y ?? (t.sy + 0.02));
                if (!p) continue;
                const b = document.createElement('button');
                b.type = 'button';
                b.className = 'ms-chip';
                b.style.left = Math.round(p.x) + 'px';
                b.style.top = Math.round(p.y) + 'px';
                b.textContent = (l.label || '↪') + ' → ' + (l.to + 1);
                b.title = S.t('chip_title', { page: l.to + 1 });
                b.addEventListener('pointerdown', (ev) => ev.stopPropagation());
                b.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    if (R.locked) return;
                    jumpTo(R.starts[e.k] + l.to, e.p + 1);
                });
                box.appendChild(b);
            }
        }
        if (R.backChip) box.appendChild(R.backChip);
    }

    function jumpTo(i, fromPageLabel) {
        const snap = snapshot();
        if (!R.nav.goTo(i)) return;
        R.turns++;
        animate(snap, 1, 'jump');
        showBack(fromPageLabel);
        R.render();
        saveLast();
    }

    function showBack(label) {
        clearTimeout(R.backT);
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ms-chip ms-chip-back';
        b.textContent = S.t('back_to', { page: label });
        b.addEventListener('pointerdown', (ev) => ev.stopPropagation());
        b.addEventListener('click', (ev) => { ev.stopPropagation(); R.backChip = null; turn(-1, 'button'); });
        R.backChip = b;
        R.backT = setTimeout(() => { R.backChip = null; placeChips(); }, 12000);
    }

    /* ── turning ─────────────────────────────────────────────────────── */

    function snapshot() {
        const a = S.settings.anim;
        if (a === 'none' || S.reduced) return null;
        const f = els.fx, x = f.getContext('2d');
        x.setTransform(1, 0, 0, 1, 0, 0);
        x.clearRect(0, 0, f.width, f.height);
        x.drawImage(els.page, 0, 0);
        x.drawImage(els.ink, 0, 0);
        return true;
    }

    function animate(snap, dir, via) {
        if (!snap) return;
        const f = els.fx;
        const slide = S.settings.anim === 'slide' && via !== 'half' && via !== 'turn-half';
        f.style.transition = 'none';
        f.style.opacity = '1';
        f.style.transform = 'none';
        f.hidden = false;
        // force the start state before the transition
        void f.offsetWidth;
        f.style.transition = slide ? 'transform .22s ease-in, opacity .22s ease-in' : 'opacity .2s linear';
        if (slide) f.style.transform = `translateX(${dir > 0 ? -100 : 100}%)`;
        f.style.opacity = '0';
        clearTimeout(R.fxT);
        R.fxT = setTimeout(() => { f.hidden = true; }, 260);
    }

    function flashEdge(dir, via) {
        const el = dir > 0 ? els.edgeR : els.edgeL;
        if (!el) return;
        el.dataset.via = via;
        el.classList.remove('ms-on');
        void el.offsetWidth;
        el.classList.add('ms-on');
        clearTimeout(el.__t);
        el.__t = setTimeout(() => el.classList.remove('ms-on'), 450);
    }

    const bounce = createDebouncer(250);

    function turn(dir, via) {
        if (!R.open || !R.nav) return { moved: false };
        const wasHalf = R.nav.view().kind === 'half';
        const snap = snapshot();
        const r = dir > 0 ? R.nav.next() : R.nav.prev();
        if (!r.moved) {
            if (r.end) banner(S.t(R.setlist ? 'end_setlist' : 'end_piece'), 2200);
            flashEdge(dir, 'none');
            return r;
        }
        if (R.backChip && dir > 0) { R.backChip = null; }
        R.turns++;
        R.via[via] = (R.via[via] || 0) + 1;
        animate(snap, dir, r.via === 'half' || wasHalf ? 'half' : r.via);
        flashEdge(dir, via);
        R.render();
        if (r.via === 'piece') announcePiece();
        if (r.via === 'link' && r.link) banner('↪ ' + S.t('jumped', { page: entryOf(R.nav.state.i).p + 1 }), 1600);
        saveLast();
        maybeComplete();
        S.onTurn && S.onTurn(dir, via, r);
        return r;
    }
    R.turn = turn;

    function announcePiece() {
        const e = entryOf(R.nav.state.i);
        const P = R.pieces[e.k];
        const pos = R.pieces.length > 1 ? `${e.k + 1}/${R.pieces.length} · ` : '';
        banner(pos + P.rec.name, 2600);
        ensureAnalysis(e.k); ensureInk(e.k);
    }

    function banner(text, ms) {
        const b = els.banner;
        b.textContent = text;
        b.hidden = false;
        clearTimeout(b.__t);
        b.__t = setTimeout(() => { b.hidden = true; }, ms || 2000);
    }
    R.banner = banner;

    function maybeComplete() {
        if (R.completed || R.turns < 4) return;
        R.completed = true;
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                turns: R.turns, pieces: R.pieces.length, setlist: R.setlist ? 1 : 0,
                mode: effMode(), half: S.settings.half ? 1 : 0, crop: S.settings.crop ? 1 : 0,
                via: Object.keys(R.via).sort((a, b) => R.via[b] - R.via[a])[0] || 'tap',
                gesture: R.via.gesture ? 1 : 0, perf: R.perf ? 1 : 0,
            });
        } catch (_) { /* telemetry is optional */ }
    }

    let saveT = 0;
    function saveLast() {
        clearTimeout(saveT);
        saveT = setTimeout(() => {
            const e = R.nav && entryOf(R.nav.state.i);
            if (!e) return;
            const rec = R.pieces[e.k].rec;
            rec.lastPage = e.p;
            rec.opened = Date.now();
            S.store.put('pieces', rec).catch(() => { /* not critical */ });
        }, 800);
    }

    /* ── keys and pedals ─────────────────────────────────────────────── */

    function keysMap() { return S.settings.keys && S.settings.keys.next ? S.settings.keys : DEFAULT_KEYS; }
    R.keysMap = keysMap;

    // Keys belong to the stand while it is what the person works with: from
    // opening a piece until they click somewhere else on the page (the FAQ,
    // the search box) — then arrows and Space scroll the page again. A pedal
    // user never clicks outside; full screen always owns the keys.
    R.armed = false;
    document.addEventListener('pointerdown', (e) => {
        const reader = els.stage.closest('section') || els.stage;
        R.armed = reader.contains(e.target);
    }, true);

    function onKey(e) {
        if (!R.open) return;
        if (S.keyTap && S.keyTap(e)) return;              // pedal learning / test
        const tg = e.target;
        if (tg && (tg.isContentEditable || tg.tagName === 'TEXTAREA' || (tg.tagName === 'INPUT' && !/^(checkbox|radio|range|button)$/i.test(tg.type)))) return;
        if (e.key === 'Escape' && R.perf) { exitPerf(); return; }
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        const act = actionForKey(keyId(e), keysMap());
        if (!act) return;
        if (!R.perf && !R.armed) return;                  // the person is busy elsewhere on the page
        e.preventDefault();
        e.stopPropagation();
        if (e.repeat) return;
        const gap = S.settings.gap ?? 250;
        if (gap > 0 && !R.keyBounce(performance.now())) return;
        turn(act === 'next' ? 1 : -1, 'key');
    }
    R.keyBounce = createDebouncer(S.settings.gap ?? 250);
    R.setGap = (ms) => { R.keyBounce = createDebouncer(ms); };
    window.addEventListener('keydown', onKey, true);

    /* ── touches ─────────────────────────────────────────────────────── */

    let pd = null;
    function local(e) {
        const r = els.stage.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    function onDown(e) {
        if (!R.open) return;
        if (e.button > 0) return;
        if (R.locked) { lockedNudge(); return; }
        const p = local(e);
        if (R.mode === 'ink') {
            if (R.ink.down(e)) { try { els.stage.setPointerCapture(e.pointerId); } catch (_) { /* old */ } e.preventDefault(); }
            return;
        }
        if (R.mode === 'split') {
            const q = screenToPage(R.tiles, p.x, p.y);
            if (q && R.tiles.length === 1) { pd = { split: true, i: q.i }; try { els.stage.setPointerCapture(e.pointerId); } catch (_) { /* old */ } moveSplit(q); e.preventDefault(); }
            return;
        }
        if (R.mode === 'place') {
            const q = screenToPage(R.tiles, p.x, p.y);
            if (q && S.onPlace) S.onPlace(q);
            return;
        }
        pd = { x: p.x, y: p.y, t: performance.now(), id: e.pointerId };
    }
    function onMove(e) {
        if (R.mode === 'ink') { R.ink.move(e); return; }
        if (pd && pd.split) {
            const p = local(e);
            const q = screenToPage(R.tiles, Math.min(Math.max(p.x, R.tiles[0].dx + 1), R.tiles[0].dx + R.tiles[0].dw - 1), p.y);
            if (q) moveSplit(q);
        }
    }
    function onUp(e) {
        if (R.mode === 'ink') { R.ink.up(e); return; }
        if (!pd) return;
        const start = pd;
        pd = null;
        if (start.split) { saveSplit(start.i); return; }
        const p = local(e);
        const dx = p.x - start.x, dy = p.y - start.y, dt = performance.now() - start.t;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 700) { turn(dx < 0 ? 1 : -1, 'swipe'); return; }
        if (Math.hypot(dx, dy) > 14 || dt > 700) return;
        const zone = start.x < R.W * 0.33 ? -1 : start.x > R.W * 0.67 ? 1 : 0;
        if (zone) turn(zone, 'tap');
        else if (S.onCenterTap) S.onCenterTap();
    }

    function moveSplit(q) {
        const e = entryOf(q.i);
        const P = R.pieces[e.k];
        const m = R.metaOf(q.i);
        const v = Math.min(m.box.y + m.box.h * 0.9, Math.max(m.box.y + m.box.h * 0.1, q.v));
        if (!P.an[e.p]) P.an[e.p] = { piece: P.rec.id, page: e.p };
        P.an[e.p].splitUser = Math.round(v * 10000) / 10000;
        schedule();
    }
    function saveSplit(i) {
        const e = entryOf(i);
        if (!e) return;
        const rec = R.pieces[e.k].an[e.p];
        if (rec && rec.prof) S.store.put('analysis', rec).catch(() => { /* cache */ });
    }
    R.resetSplit = function () {
        const e = entryOf(R.nav.state.i);
        const rec = e && R.pieces[e.k].an[e.p];
        if (rec) { delete rec.splitUser; if (rec.prof) S.store.put('analysis', rec).catch(() => {}); schedule(); }
    };

    function lockedNudge() {
        if (S.onLockedTouch) S.onLockedTouch();
    }

    els.stage.addEventListener('pointerdown', onDown);
    els.stage.addEventListener('pointermove', onMove);
    els.stage.addEventListener('pointerup', onUp);
    els.stage.addEventListener('pointercancel', () => { pd = null; if (R.mode === 'ink') R.ink.up(); });
    // a right-click menu or a long-press callout over the music is never wanted on stage
    els.stage.addEventListener('contextmenu', (e) => e.preventDefault());

    /* ── modes ───────────────────────────────────────────────────────── */

    R.setMode = function (m) {
        R.mode = m;
        els.stage.dataset.mode = m;
        schedule();
    };

    R.setLocked = function (on) {
        R.locked = !!on;
        els.stage.classList.toggle('ms-locked', R.locked);
    };

    /* ── performance view ────────────────────────────────────────────── */

    function fsEl() { return document.fullscreenElement || document.webkitFullscreenElement || null; }

    async function enterPerf() {
        if (!R.open) return;
        R.perf = true;
        els.stage.classList.add('ms-perf');
        const ok = window.__safeFullscreen ? await window.__safeFullscreen(els.stage, {}) : false;
        R.pseudo = !ok;
        els.stage.classList.toggle('ms-pseudo', R.pseudo);
        if (R.pseudo) document.documentElement.classList.add('ms-noscroll');
        S.onPerf && S.onPerf(true);
        setTimeout(() => { R.segCache.clear(); R.render(); }, 60);
        try { window.__stage && window.__stage('performance', { pseudo: R.pseudo ? 1 : 0 }); } catch (_) { /* optional */ }
    }
    function exitPerf() {
        if (!R.perf) return;
        R.perf = false;
        els.stage.classList.remove('ms-perf', 'ms-pseudo');
        document.documentElement.classList.remove('ms-noscroll');
        if (fsEl()) {
            try {
                const p = (document.exitFullscreen || document.webkitExitFullscreen).call(document);
                if (p && p.catch) p.catch(() => { /* already out */ });
            } catch (_) { /* already out */ }
        }
        S.onPerf && S.onPerf(false);
        setTimeout(() => { R.segCache.clear(); R.render(); }, 60);
    }
    R.enterPerf = enterPerf;
    R.exitPerf = exitPerf;
    const onFs = () => { if (R.perf && !R.pseudo && !fsEl()) exitPerf(); };
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);

    try {
        new ResizeObserver(() => { if (R.open) schedule(); }).observe(els.stage);
    } catch (_) { window.addEventListener('resize', () => { if (R.open) schedule(); }); }

    /* ── messages on the stage ───────────────────────────────────────── */

    function showMsg(text) {
        els.msg.textContent = text || '';
        els.msg.hidden = !text;
    }
    R.showMsg = showMsg;

    /* ── open / close ────────────────────────────────────────────────── */

    R.openSession = async function (recs, opts = {}) {
        R.close(true);
        R.pieces = recs.map((rec) => ({ rec, src: null, srcP: null, an: new Array(rec.pages), boxes: null, ink: new Map(), anP: null, inkP: null }));
        R.seq = []; R.starts = [];
        R.pieces.forEach((P, k) => { R.starts.push(R.seq.length); for (let p = 0; p < P.rec.pages; p++) R.seq.push({ k, p }); });
        R.setlist = opts.setlist || null;
        R.nav = new Navigator(ctx);
        R.turns = 0; R.via = {}; R.completed = false; R.backChip = null;
        R.open = true;
        R.armed = true;
        showMsg('');
        measure();
        const startK = opts.k || 0;
        const startP = Math.min(Math.max(0, opts.page || 0), recs[startK].pages - 1);
        R.nav.goTo(R.starts[startK] + startP, { push: false, resetPasses: true });
        ensureAnalysis(startK);
        ensureInk(startK);
        R.render();
        if (R.pieces[startK + 1]) setTimeout(() => ensureSource(startK + 1).catch(() => {}), 1500);
        return true;
    };

    R.close = function (quiet) {
        if (R.perf) exitPerf();
        R.open = false;
        for (const P of R.pieces) { try { if (P.src) P.src.close(); } catch (_) { /* gone */ } }
        for (const v of R.cache.values()) if (v.c && v.c.width) v.c.width = 0;
        R.cache.clear(); R.pending.clear(); R.queue.length = 0;
        R.pieces = []; R.seq = []; R.tiles = [];
        R.nav = null;
        if (!quiet) S.onClose && S.onClose();
    };

    R.current = function () {
        if (!R.nav) return null;
        const i = R.nav.state.i;
        const e = entryOf(i);
        if (!e) return null;
        const P = R.pieces[e.k];
        return { i, k: e.k, p: e.p, piece: P.rec, pages: P.rec.pages, view: R.nav.view(), mode: effMode() };
    };

    R.goTo = function (i) { const snap = snapshot(); if (R.nav.goTo(i)) { animate(snap, 1, 'jump'); R.render(); saveLast(); } };
    R.restart = function () { R.nav.resetPasses(); R.goTo(R.starts[entryOf(R.nav.state.i).k]); R.nav.resetPasses(); };
    R.relayout = function () { R.segCache.clear(); if (R.nav) R.nav.refresh(); schedule(); };
    R.effMode = effMode;
    R.ensureAnalysis = ensureAnalysis;

    R.ink = createInk(R, S);
    return R;
}
